import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuth } from '../../../context/AuthContext';
import { RootState } from '../../../store';

interface RequireAuthProps {
  children: React.ReactNode;
}

/**
 * RequireAuth component
 * Wraps individual routes that require authentication AND websocket connection
 * Redirects to login if user is not authenticated, then returns to the original page after login
 * Blocks access until websocket is connected
 */
const RequireAuth: React.FC<RequireAuthProps> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const socketConnected = useSelector((state: RootState) => state.ui.socketConnected);
  const location = useLocation();

  // Don't redirect while checking auth status
  if (loading) {
    return null; // Or show a small loading indicator if desired
  }

  // If not authenticated, redirect to login with return path
  if (!isAuthenticated) {
    // Save the current location they were trying to access
    // Use replace=true to avoid creating a history entry for the protected page
    return <Navigate to="/auth/login" state={{ from: location }} replace={true} />;
  }

  // If authenticated but websocket not connected, show loading/waiting state
  if (!socketConnected) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Connecting to server...</p>
        </div>
      </div>
    );
  }

  // User is authenticated AND websocket is connected, render the protected content
  return <>{children}</>;
};

export default RequireAuth;
